<template>
  <div class="analytics-overview-doughnut-chart">
    <DoughnutChartElement
      class="analytics-overview-doughnut-chart__chart"
      ref="doughnutChart"
      :key="totalNumber"
      :data="dataDoughnutChart"
    />
    <div class="analytics-overview-doughnut-chart__container">
      <div class="analytics-overview-doughnut-chart__total-block">
        <span class="analytics-overview-doughnut-chart__total-number">
          {{
            analyticsSelectedOption === KPIKeys.AVERAGE_CPC ||
            analyticsSelectedOption === KPIKeys.EXPENSES
              ? formatCurrency(totalNumber)
              : numberWithSpacesCeilRound(totalNumber).toLocaleString('ru-RU')
          }}
        </span>
        <span
          v-if="totalPercent !== null"
          class="analytics-overview-doughnut-chart__total-percent"
          :style="{ color: totalPercentColor }"
          >{{ numberWithSpacesCeilRound(totalPercent).toLocaleString('ru-RU') }}%</span
        >
      </div>
      <ul class="analytics-overview-doughnut-chart__list">
        <li
          v-for="{ service, count, percent, color, name } in servicesList"
          :key="service"
          class="analytics-overview-doughnut-chart__list-item"
        >
          <span
            class="analytics-overview-doughnut-chart__point"
            :style="{ background: color }"
          ></span>
          <span class="analytics-overview-doughnut-chart__service">
            {{ name }}
          </span>
          <span class="analytics-overview-doughnut-chart__count">
            {{
              analyticsSelectedOption === KPIKeys.AVERAGE_CPC ||
              analyticsSelectedOption === KPIKeys.EXPENSES
                ? formatCurrency(count, getCurrencyCode(service))
                : numberWithSpacesCeilRound(count).toLocaleString('ru-RU')
            }}
          </span>
          <span
            v-if="percent !== null"
            class="analytics-overview-doughnut-chart__percent"
            :style="{ color: percent > 0 ? '#27AE60' : '#EB5757' }"
            >{{ numberWithSpacesCeilRound(percent).toLocaleString('ru-RU') }}%</span
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { getServiceColor, getServiceName, ServiceType } from '@/utils/services';
import numberWithSpacesCeilRound from '@/mixin/numberWithSpaces';
import { AnalyticsType, KPIKeys } from '@/utils/common';
import { formatCurrency } from '@/utils/helpers';

export default {
  name: 'AnalyticsOverviewDoughnutChart',
  components: {
    DoughnutChartElement: () => import('@/components/elements/DoughnutChartElement'),
  },
  mixins: [numberWithSpacesCeilRound],
  props: {
    dataDoughnutChart: { type: Object, required: true },
    totalNumber: { type: Number, required: true },
    totalPercent: { type: Number, required: false, default: null },
    dataList: { type: Array, required: true },
  },
  data: () => ({
    KPIKeys,
    ServiceType,
  }),
  computed: {
    getCurrencyCode() {
      return (service) => {
        const s = service.toUpperCase();
        if (s === ServiceType.TELEGRAM) return 'EUR';
        if (s === ServiceType.GOOGLE) return 'USD';
        return 'RUB';
      };
    },
    analyticsSelectedOption() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].selectedOption;
    },
    overviewAnalyticsData() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].data;
    },
    totalPercentColor() {
      return this.numberWithSpacesCeilRound(this.totalPercent) > 0 ? '#27AE60' : '#EB5757';
    },
    servicesList() {
      return Object.keys(this.overviewAnalyticsData).map((service) => {
        const item = this.dataList.find((i) => i.service === service) || { count: 0, percent: 0 };
        return {
          service,
          count: item.count,
          percent: this.totalPercent === null ? null : item.percent,
          color: getServiceColor(service),
          name: getServiceName(service),
        };
      });
    },
  },
  methods: {
    formatCurrency,
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.analytics-overview-doughnut-chart {
  display: flex;
  justify-content: space-between;
  gap: 10px;

  &__count,
  &__percent {
    font-weight: 400;
    font-size: 12px;
  }

  &__count {
    margin-right: 10px;
    color: $gray;
  }

  &__service {
    padding-right: 10px;
    flex: 1 1 auto;
    font-weight: 400;
    font-size: 14px;
    color: $basicBlack;
  }

  &__point {
    position: absolute;
    width: 10px;
    height: 10px;
    left: 0;
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
  }

  &__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding-left: 16px;
    margin-bottom: 6px;
  }

  &__total-percent {
    font-weight: 400;
    font-size: 16px;
  }

  &__total-number {
    font-weight: 700;
    font-size: 20px;
  }

  &__total-block {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__container {
    max-width: 264px;
    width: 100%;
  }

  &__chart {
    width: 150px;
    height: 150px;

    @include below(475px) {
      width: 100px;
      height: 100px;
    }
  }
}
</style>
